import { useState } from 'react';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { listDeparturePaymentEligible } from '../../api/readyToFly';
import { listLookup } from '../../api/masterData';
import { useBranchFilter } from '../../context/branchContext';
import { TableToolbar } from '../../components/table/TableToolbar';
import { DepartureBatchFilter, DeparturePaymentEligibleStudent } from '../../types/readyToFly';
import { DepartureExportButtons } from './DepartureExportButtons';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong.') : 'Something went wrong.';

const formatAmount = (amount: number | null, currency: { code: string } | null) =>
  amount == null
    ? '—'
    : `${currency?.code ?? ''} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—');

/** The four items, each with its confirmed paid amount — every row here is
 * fully paid by definition (the backend only returns eligible students), so
 * this is the evidence behind the "4/4 paid" chip, not a status to act on. */
const PaymentSummaryChip = ({ items }: { items: DeparturePaymentEligibleStudent['payment']['items'] }) => (
  <Tooltip
    title={
      <Box>
        {items.map((item) => (
          <Typography key={item.name} variant="caption" component="div">
            {item.name}: {formatAmount(item.paidAmount, item.currency)} paid
          </Typography>
        ))}
      </Box>
    }
  >
    <Chip
      size="small"
      color="success"
      label={`${items.filter((i) => i.status === 'PAID').length}/${items.length} paid`}
    />
  </Tooltip>
);

export type FullyPaidStudentsVariant = 'batches' | 'readyToFly';

interface FullyPaidStudentsTableProps {
  /** Which view's columns to show — the rows (and the rule behind them)
   * are identical either way. */
  variant: FullyPaidStudentsVariant;
}

/** Shared by Operations → Batches to Departures and Ready to Fly → Fully
 * Paid. Both read the same backend list (`/ready-to-fly/departure-payment-
 * eligible`): students with Registration Fee, IMC professional handling fee
 * and Document Processing Fee fully paid incl. SSCL, plus Student Welfare
 * Fee — confirmed payments only. Read-only: nothing here changes a batch,
 * visa or departure. */
export const FullyPaidStudentsTable = ({ variant }: FullyPaidStudentsTableProps) => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [search, setSearch] = useState('');
  const [universityId, setUniversityId] = useState<number | ''>('');
  const [intakeId, setIntakeId] = useState<number | ''>('');
  const [batch, setBatch] = useState<DepartureBatchFilter>('ALL');

  const universitiesQuery = useQuery({
    queryKey: ['lookup', '/master-data/universities'],
    queryFn: () => listLookup('/master-data/universities', { activeOnly: true }),
  });
  const intakesQuery = useQuery({
    queryKey: ['lookup', '/master-data/intakes'],
    queryFn: () => listLookup('/master-data/intakes', { activeOnly: true }),
  });

  const listQuery = useQuery({
    queryKey: [
      'departure-payment-eligible',
      paginationModel.page,
      paginationModel.pageSize,
      search,
      universityId,
      intakeId,
      batch,
      selectedBranchId,
    ],
    queryFn: () =>
      listDeparturePaymentEligible({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
        universityId: universityId || undefined,
        intakeId: intakeId || undefined,
        branchId: selectedBranchId,
        batch,
      }),
    placeholderData: (prev) => prev,
  });

  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));

  const commonColumns: GridColDef<DeparturePaymentEligibleStudent>[] = [
    {
      field: 'studentNumber',
      headerName: 'Student #',
      width: 120,
      valueGetter: (_v, row) => row.student.studentNumber,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, row) => `${row.student.firstName} ${row.student.lastName}`,
    },
    { field: 'branch', headerName: 'Branch', width: 130, valueGetter: (_v, row) => row.branch?.name ?? '—' },
    {
      field: 'university',
      headerName: 'University',
      width: 190,
      valueGetter: (_v, row) => row.university?.name ?? '—',
    },
    { field: 'intake', headerName: 'Intake', width: 130, valueGetter: (_v, row) => row.intake?.name ?? '—' },
    {
      field: 'payment',
      headerName: 'Payments',
      width: 110,
      sortable: false,
      renderCell: (params) => <PaymentSummaryChip items={params.row.payment.items} />,
    },
  ];

  const variantColumns: GridColDef<DeparturePaymentEligibleStudent>[] =
    variant === 'batches'
      ? [
          {
            field: 'batch',
            headerName: 'Batch',
            width: 150,
            renderCell: (params) =>
              params.row.batch ? (
                <Chip size="small" label={params.row.batch.batchNumber} />
              ) : (
                <Chip size="small" variant="outlined" color="warning" label="Not assigned" />
              ),
          },
          {
            field: 'departureDate',
            headerName: 'Departure',
            width: 120,
            valueGetter: (_v, row) => formatDate(row.departureDate),
          },
        ]
      : [
          { field: 'visa', headerName: 'Visa', width: 120, valueGetter: (_v, row) => row.visaStatus?.name ?? 'Not Applied' },
          {
            field: 'batch',
            headerName: 'Batch',
            width: 120,
            valueGetter: (_v, row) => row.batch?.batchNumber ?? '—',
          },
          {
            field: 'flight',
            headerName: 'Flight',
            width: 140,
            valueGetter: (_v, row) =>
              row.travelDetail?.airline ? `${row.travelDetail.airline} ${row.travelDetail.flightNumber ?? ''}`.trim() : '—',
          },
          {
            field: 'departureStatus',
            headerName: 'Departure',
            width: 120,
            valueGetter: (_v, row) => (row.travelDetail?.departureStatus ?? '—').replace(/_/g, ' '),
          },
        ];

  const hasFilters = !!search || !!universityId || !!intakeId || batch !== 'ALL';

  const NoRows = () => (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', px: 2, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {hasFilters
          ? 'No fully paid students match these filters.'
          : 'No students have fully paid all four required items yet.'}
      </Typography>
    </Stack>
  );

  return (
    <Box>
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Students with Registration Fee, IMC Professional Handling Fee and Document Processing Fee fully paid (including
        SSCL) and Student Welfare Fee fully paid. Only confirmed payments count. An uploaded payment slip awaiting
        confirmation does not.
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <TextField
          placeholder="Search student name or #…"
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="University"
          sx={{ minWidth: 170 }}
          value={universityId}
          onChange={(e) => {
            setUniversityId(e.target.value ? Number(e.target.value) : '');
            resetPage();
          }}
        >
          <MenuItem value="">All</MenuItem>
          {universitiesQuery.data?.map((u) => (
            <MenuItem key={u.id as number} value={u.id as number}>
              {String(u.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Intake"
          sx={{ minWidth: 150 }}
          value={intakeId}
          onChange={(e) => {
            setIntakeId(e.target.value ? Number(e.target.value) : '');
            resetPage();
          }}
        >
          <MenuItem value="">All</MenuItem>
          {intakesQuery.data?.map((i) => (
            <MenuItem key={i.id as number} value={i.id as number}>
              {String(i.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Batch"
          sx={{ minWidth: 150 }}
          value={batch}
          onChange={(e) => {
            setBatch(e.target.value as DepartureBatchFilter);
            resetPage();
          }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="UNBATCHED">Not in a batch</MenuItem>
          <MenuItem value="BATCHED">In a batch</MenuItem>
        </TextField>
      </Stack>

      {variant === 'batches' && (
        <DepartureExportButtons
          filters={{
            search: search || undefined,
            universityId: universityId || undefined,
            intakeId: intakeId || undefined,
            branchId: selectedBranchId,
            batch,
          }}
        />
      )}

      {listQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load fully paid students: {extractErrorMessage(listQuery.error)}
        </Alert>
      ) : (
        <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={[...commonColumns, ...variantColumns]}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            slots={{ toolbar: TableToolbar, noRowsOverlay: NoRows }}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/students/${params.row.studentId}`)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}
    </Box>
  );
};
